import React, {useState, useEffect} from 'react';
import {
  Card,
  Button,
  Typography,
  IconButton,
  TextField,
  Box,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Snackbar
} from '@mui/material';
import {
  DndContext,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  closestCenter
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import Rating from '@mui/material/Rating';
import './dragdrop.css'; // Import the CSS file
import EncryptUtils from 'src/utils/EncryptUtils';
import {getLMSResultsData} from '@shared/services/service-handlers/PrivateService';
import {useAppContext} from '@context/AppContext';
import {useOnlineContext} from './OnlineTestContext';
import {ConfirmDialog} from '@components/custom-dialog';
import {useBoolean} from 'src/hooks/use-boolean';
import {LoadingButton} from '@mui/lab';
import { getErrorMessage } from '@shared/utils/ErrorUtils';
import { useSnackbar } from 'notistack';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import { BASE_RECORD_ID } from 'src/routes/BaseRoute';

interface Choice {
  id: number;
  OptionText: string;
  is_single_multiple_trueOrFalse_correct_option: boolean;
}

interface MatchChoice {
  id: number;
  match_the_following_left_value: string;
  match_the_following_right_value: string;
}
interface OptionsExtra {
  id: number;
  match_the_following_additional_right_value: string;
}
interface CustomIcon {
  icon: React.ReactElement;
  label: string;
  id: number;
  key: string;
}
interface CustomIcons {
  [key: string]: CustomIcon;
}
interface RatingScaleConfig {
  ratingScale: number;
  ratingScaleType: string;
  customIcons: CustomIcons;
}
interface npsString {
  likely: string;
  notlikely: string;
}

interface BaseQuestion {
  id: number;
  QuestionText: string;
  QuestionMarks_id?: number;
  QuestionType_id: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  QuestionCategoryType_id?: string;
  QuestionDifficultyLevel_id?: string;
  csv_QuestionTags?: string;
  ADDQuestionToQuestionBank?: boolean;
  disableComments?: boolean;
  makecommentmandatory?: boolean;
  markasMandatory?: boolean;
  ModuleOnlineTestList_id?: number;
  sequence: number;
}

interface SingleQuestion extends BaseQuestion {
  QuestionType_id: 1;
  correctAnswer?: number;
  choices: Choice[];
}

interface MultipleQuestion extends BaseQuestion {
  QuestionType_id: 2;
  correctAnswers?: number[];
  choices: Choice[];
}

interface TrueFalseQuestion extends BaseQuestion {
  QuestionType_id: 3;
  TrueFalse: boolean;
}

interface MatchingQuestion extends BaseQuestion {
  QuestionType_id: 4;
  correctMatches: MatchChoice[];
  additionalChoices: OptionsExtra[];
}

interface DescriptiveQuestion extends BaseQuestion {
  QuestionType_id: 5;
  descriptive_enforce_word_limit: boolean;
  descriptive_enforce_word_limit_length?: number;
}
interface RatingScaleQuestion extends BaseQuestion {
  QuestionType_id: 6;
  ratingScale: RatingScaleConfig;
}
interface StarQuestion extends BaseQuestion {
  QuestionType_id: 7;
  starRating?: number;
}
interface NPSQuestion extends BaseQuestion {
  QuestionType_id: 8;
  npsstring: npsString;
}

type Question =
  | SingleQuestion
  | MultipleQuestion
  | TrueFalseQuestion
  | MatchingQuestion
  | DescriptiveQuestion
  | RatingScaleQuestion
  | NPSQuestion
  | StarQuestion;

interface DragdropTypeProps {
  questiondata?: Question[];
  handleEditQuestion?: (value: any) => void;
}

const DragDropQuestion: React.FC<DragdropTypeProps> = ({
  questiondata,
  handleEditQuestion
}) => {
  const {
    appState: {applicationName}
  } = useAppContext();
  const uniqueId = EncryptUtils.getDecryptedData(
location?.search,
BASE_RECORD_ID
);
  const {enqueueSnackbar} = useSnackbar();
  const {uiFields, setUiFields} = useOnlineContext();
  const {questionsData, setQuestionData} = useOnlineContext();
  // const [questions, setQuestions] = useState<Question[]>(questiondata);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(
    null
  );
  const confirm = useBoolean();
  const [deletLoader, setDeleteLoader] = useState(false);
  const [newQuestionText, setNewQuestionText] = useState('');
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(
    null
  );
  const handleQuestionDelete = async (datavalue: any, operation: string) => {
    try {
      const operations = operation;
      // if (operation === 'UPDATE') {
      //   datavalue.id = onlineData.inserted_id;
      // }
      const {sql_data} = uiFields['add_question_crud'];
      // datavalue.bloburls = datavalue.bloBurls[0].blobUrl;
      setDeleteLoader(true);
      const data: any = {
        sql_data: sql_data,
        data: {id: datavalue,   course_overview_id: uniqueId},
        operation: operations
      };
      const encryptRequestData = EncryptUtils.encryptedData(data);
      const result = await getLMSResultsData(applicationName, 'LMS', {
        tk: encryptRequestData
      });
      const transformedResult = result.data.map((item: any) => {
        return {
          ...item,
          // Parse JSON fields or set them to empty arrays if they are null
          choices: item.choices ? JSON.parse(item.choices) : null,
          correctMatches: item.correctMatches
            ? JSON.parse(item.correctMatches)
            : null,
          additionalChoices: item.additionalChoices
            ? JSON.parse(item.additionalChoices)
            : null,
          descriptive_enforce_word_limit: item.descriptive_enforce_word_limit
            ? JSON.parse(item.descriptive_enforce_word_limit)
                .descriptive_enforce_word_limit
            : null,
          // Parse the descriptive_enforce_word_limit_length JSON or set it to null
          descriptive_enforce_word_limit_length:
            item.descriptive_enforce_word_limit_length
              ? JSON.parse(item.descriptive_enforce_word_limit_length)
                  .descriptive_enforce_word_limit_length
              : null,
          TrueFalse: item.TrueFalse
            ? JSON.parse(item.TrueFalse).TrueFalse
            : null
          // Add any additional transformations here if needed
        };
      });

      setQuestionData(transformedResult);

      // setQuestionData(result.data[0]);
      setDeleteLoader(false);
      // if (operation === 'INSERT') {
      //   datavalue.id = result.data[0].id; // Assign the inserted ID from the result
      //   if (result.data[0].correctMatches) {
      //     datavalue.correctMatches = JSON.parse(
      //       result.data[0].correctMatches
      //     ).map(
      //       (item: {
      //         id: any;
      //         match_the_following_left_value: any;
      //         match_the_following_right_value: any;
      //         sequence: any;
      //       }) => ({
      //         id: item.id,
      //         match_the_following_left_value:
      //           item.match_the_following_left_value,
      //         match_the_following_right_value:
      //           item.match_the_following_right_value
      //       })
      //     );
      //   }

      //   // Parse and replace values for choices
      //   if (result.data[0].choices) {
      //     datavalue.choices = JSON.parse(result.data[0].choices).map(
      //       (item: {
      //         id: any;
      //         OptionText: any;
      //         OptionDescription: any;
      //         is_single_multiple_trueOrFalse_correct_option: any;
      //       }) => ({
      //         id: item.id,
      //         OptionText: item.OptionText,
      //         OptionDescription: item.OptionDescription,
      //         is_single_multiple_trueOrFalse_correct_option:
      //           item.is_single_multiple_trueOrFalse_correct_option
      //       })
      //     );
      //   }

      //   // Parse and replace values for additionalChoices
      //   if (result.data[0].additionalChoices) {
      //     datavalue.additionalChoices = JSON.parse(
      //       result.data[0].additionalChoices
      //     ).map(
      //       (item: {
      //         id: any;
      //         match_the_following_additional_right_value: any;
      //       }) => ({
      //         id: item.id,
      //         match_the_following_additional_right_value:
      //           item.match_the_following_additional_right_value
      //       })
      //     );
      //   }
      //   datavalue.sequence = result.data[0].sequence; // Assign the inserted sequence from the result
      //   return datavalue;
      // } else {
      //   if (result.data[0].correctMatches) {
      //     datavalue.correctMatches = JSON.parse(
      //       result.data[0].correctMatches
      //     ).map(
      //       (item: {
      //         id: any;
      //         match_the_following_left_value: any;
      //         match_the_following_right_value: any;
      //         sequence: any;
      //       }) => ({
      //         id: item.id,
      //         match_the_following_left_value:
      //           item.match_the_following_left_value,
      //         match_the_following_right_value:
      //           item.match_the_following_right_value
      //       })
      //     );
      //   }

      //   // Parse and replace values for choices
      //   if (result.data[0].choices) {
      //     datavalue.choices = JSON.parse(result.data[0].choices).map(
      //       (item: {
      //         id: any;
      //         OptionText: any;
      //         OptionDescription: any;
      //         is_single_multiple_trueOrFalse_correct_option: any;
      //       }) => ({
      //         id: item.id,
      //         OptionText: item.OptionText,
      //         OptionDescription: item.OptionDescription,
      //         is_single_multiple_trueOrFalse_correct_option:
      //           item.is_single_multiple_trueOrFalse_correct_option
      //       })
      //     );
      //   }

      //   // Parse and replace values for additionalChoices
      //   if (result.data[0].additionalChoices) {
      //     datavalue.additionalChoices = JSON.parse(
      //       result.data[0].additionalChoices
      //     ).map(
      //       (item: {
      //         id: any;
      //         match_the_following_additional_right_value: any;
      //       }) => ({
      //         id: item.id,
      //         match_the_following_additional_right_value:
      //           item.match_the_following_additional_right_value
      //       })
      //     );
      //   }
      //   datavalue.sequence = result.data[0].sequence;
      //   return datavalue;
      // }
      // Send data to the server
      confirm.onFalse();
      setDeleteId(null);
    } catch (e) {
      setDeleteLoader(false);
      confirm.onFalse();
      setDeleteId(null);
    }
  };
  const [saved, setSaved] = useState<boolean>(false);
  const handleDeleteQuestion = async (id: number) => {
    await handleQuestionDelete(id, 'DELETE');
    setQuestionData((prevQuestions: any[]) =>
      prevQuestions.filter((question: {id: number}) => question.id !== id)
    );
  };
  const handleSnackbarClose = () => {
    // setOpenSnackbar(false);
    setSaved(false);
  };
  // useEffect(() => {
  //   setQuestions(questiondata);
  // }, [questiondata]);
  const handleSequenceEdit = async (
    fromIndex: number,
    toIndex: number,
    id: number,
    setID?: number
  ) => {
    try {
      const operations = 'UPDATESEQUENCE';
      // if (operation === 'UPDATE') {
      //   datavalue.id = onlineData.inserted_id;
      // }
      const {sql_data} = uiFields['add_question_crud'];
      // datavalue.bloburls = datavalue.bloBurls[0].blobUrl;
      // setQuestionLoader(true);
      const data: any = {
        sql_data: sql_data,
        data: {
          id: id,
          ModuleOnlineTestList_id: setID,
          OldSequence: fromIndex,   course_overview_id: uniqueId,
          NewSequence: toIndex
        },
        operation: operations
      };
      const encryptRequestData = EncryptUtils.encryptedData(data);
      const result = await getLMSResultsData(applicationName, 'LMS', {
        tk: encryptRequestData
      });
      setSaved(true);
      // setQuestionData(result.data[0]);
      // setQuestionLoader(false);
      // if (operation === 'INSERT') {
      //   datavalue.id = result.data[0].id; // Assign the inserted ID from the result
      //   datavalue.sequence = result.data[0].sequence; // Assign the inserted sequence from the result
      //   return datavalue;
      // } else {
      //   return true;
      // }
      // Send data to the server
      enqueueSnackbar("Sequence is Updated",  SnackBarConfig.getSuccess());
    } catch (e) {
      // console.log('ee', e);
      // setQuestionLoader(false);
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragEnd = async (event: any) => {
    const {active, over} = event;

    if (!active || !over) {
      console.warn('Active or Over element is missing from the event:', event);
      return;
    }

    if (active.id !== over.id) {
      setQuestionData((items: any[]) => {
        const oldIndex = items.findIndex(
          (item: {id: any}) => item.id == active.id
        );
        const newIndex = items.findIndex(
          (item: {id: any}) => item.id == over.id
        );

        // Ensure valid indices
        if (oldIndex === -1 || newIndex === -1) {
          console.error('Invalid indices for drag operation:', {
            oldIndex,
            newIndex
          });
          return items;
        }

        // Update the items array with new order
        const updatedItems = arrayMove(items, oldIndex, newIndex);

        // Update the sequence for all items
        const reindexedItems = updatedItems.map((item, index) => ({
          ...item,
          sequence: index + 1
        }));

        // Call the API with oldIndex, newIndex, and updated state
        handleSequenceEdit(
          oldIndex + 1,
          newIndex + 1,
          active.id,
          questionsData[0].ModuleOnlineTestList_id
        );

        return reindexedItems;
      });
    }
  };

  const handleQuestionNumberChange = async (
    fromIndex: number,
    toIndex: string,
    id: number,
    setID?: number
  ) => {
    const toIndexParsed = parseInt(toIndex);
    const toIndexCorrected = toIndexParsed;

    // Validate the input indexes
    if (
      fromIndex - 1 < 0 ||
      fromIndex > questionsData.length ||
      toIndexCorrected - 1 < 0 ||
      toIndexCorrected > questionsData.length
    ) {
      console.error('Index out of bounds');
      return;
    }

    if (fromIndex === toIndexCorrected) {
      return;
    }

    // Move the question in the array
    const updatedQuestions = [...questionsData];
    const [movedObject] = updatedQuestions.splice(fromIndex - 1, 1);
    updatedQuestions.splice(toIndexCorrected - 1, 0, movedObject);
    // Update the sequence values for all questions
    const reindexedQuestions = updatedQuestions.map((question, index) => ({
      ...question,
      sequence: index + 1
    }));

    setQuestionData(reindexedQuestions);

    // Call the API or handler to persist the changes
    await handleSequenceEdit(fromIndex, toIndexParsed, id, setID);
  };

  const handleQuestionTextChange = (id: string, newText: string) => {
    setQuestionData((prevQuestions: any[]) =>
      prevQuestions.map((question: {id: number}) =>
        question.id === Number(id) ? {...question, question: newText} : question
      )
    );
  };

  const SortableItem: React.FC<{id: number; children: any}> = ({
    id,
    children
  }) => {
    const {attributes, listeners, setNodeRef, transform, transition} =
      useSortable({id});
    const style = {
      transform: CSS.Transform.toString(transform),
      transition
    };

    return (
      <div ref={setNodeRef} style={style}>
        {children({attributes, listeners})}
      </div>
    );
  };

  // const handleEditQuestion = (question: any) => {
  //   setEditingQuestionId(id);
  //   const question = questions.find(q => q.id === id);
  //   if (question) setNewQuestionText(question.question);
  // };

  const handleSaveQuestion = () => {
    if (editingQuestionId) {
      handleQuestionTextChange(editingQuestionId, newQuestionText);
      setEditingQuestionId(null);
      setNewQuestionText('');
    }
  };

  const handleQuestionId = (id: string) => {
    if (selectedQuestionId === id) {
      setSelectedQuestionId(null);
    } else {
      setSelectedQuestionId(id);
    }
  };
  const getIconsForScale = (scale: number, type: any) => {
    const icons = type;
    switch (scale) {
      case 3:
        return [icons[1], icons[4], icons[7]];
      case 5:
        return [icons[1], icons[2], icons[4], icons[5], icons[7]];
      case 7:
        return [
          icons[1],
          icons[2],
          icons[3],
          icons[4],
          icons[5],
          icons[6],
          icons[7]
        ];
      default:
        return [];
    }
  };
  const getColor = (index: number) => {
    if (index <= 6) return '#f44336'; // Red
    if (index === 7 || index === 8) return '#ffeb3b'; // Yellow
    return '#4caf50'; // Green
  };

  const renderQuestionOptions = (question: Question) => {
    switch (question.QuestionType_id) {
      case 1:
        return question.choices.map((option, index) => (
          <FormControlLabel
            key={index}
            control={
              <Radio
                checked={option.is_single_multiple_trueOrFalse_correct_option}
              />
            }
            label={option.OptionText}
          />
        ));
      case 2:
        return question.choices.map((option, index) => (
          <FormControlLabel
            key={index}
            control={
              <Checkbox
                checked={option.is_single_multiple_trueOrFalse_correct_option}
              />
            }
            label={option.OptionText}
          />
        ));
      case 3:
        return (
          <RadioGroup>
            {[
              {Text: 'True', value: true},
              {Text: 'False', value: false}
            ].map((option, index) => (
              <FormControlLabel
                key={index}
                value={option.value}
                control={
                  <Radio checked={question.TrueFalse === option.value} />
                }
                label={option.Text}
              />
            ))}
          </RadioGroup>
        );
      case 4:
        return (
          <>
            {question.correctMatches.map((match, index) => (
              <div
                key={match.id}
                style={{display: 'flex', alignItems: 'center'}}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={match.match_the_following_left_value}
                  sx={{mb: 1, mr: 1}}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  value={match.match_the_following_right_value}
                  sx={{mb: 1}}
                />
              </div>
            ))}
            {question?.additionalChoices?.length > 0 && (
              <Typography variant="h6" style={{marginTop: 10}}>
                Additional Choices
              </Typography>
            )}
            {question.additionalChoices &&
              question.additionalChoices.map((choice, index) => (
                <div
                  key={choice.id}
                  style={{display: 'flex', justifyContent: 'flex-end'}}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={choice.match_the_following_additional_right_value}
                    sx={{mb: 1, width: '49%'}}
                  />
                </div>
              ))}
          </>
        );
      case 5:
        return (
          <>
            {question.descriptive_enforce_word_limit && (
              <>
                <Typography variant="h6" style={{marginTop: 10}}>
                  Enforce word limit
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="number"
                  value={question.descriptive_enforce_word_limit_length}
                  sx={{mb: 2}}
                />
              </>
            )}
          </>
        );
      case 6:
        return (
          <>
            <Box sx={{display: 'flex', justifyContent: 'space-around', mt: 2}}>
              {getIconsForScale(
                question.ratingScale?.ratingScale,
                question.ratingScale?.customIcons
              ).map(icon => (
                <Box key={icon.id} sx={{textAlign: 'center'}}>
                  {icon.icon}
                </Box>
              ))}
            </Box>
          </>
        );
      case 7:
        return (
          <Box sx={{paddingLeft: '4vh'}}>
            <Rating
              name="star-rating"
              value={question.starRating}
              // onChange={(event, newValue) => {
              //   setRating(newValue);
              // }}
              max={question.starRating}
              readOnly
              size="medium"
            />
          </Box>
        );
      case 8:
        return (
          <>
            <Box>
              <ToggleButtonGroup
                exclusive
                aria-label="answer score"
                fullWidth
                disabled
                sx={{
                  display: 'flex',
                  justifyContent: 'space-around',
                  flexWrap: 'wrap'
                }}>
                {[...Array(11)].map((_, index) => (
                  <ToggleButton
                    key={index}
                    value={index}
                    sx={{
                      flex: '1 0 9%',
                      backgroundColor: getColor(index),
                      color: 'black !important',
                      maxWidth: '55px'
                    }}>
                    {index}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 1
              }}>
              <p>{question.npsstring.likely}</p>
              <p>{question.npsstring.notlikely}</p>
            </Box>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Card>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}>
          <SortableContext
            items={questionsData}
            strategy={verticalListSortingStrategy}>
            {questionsData.map((question: Question, index: any) => (
              <SortableItem key={question.id} id={question.id}>
                {({attributes, listeners}: any) => (
                  <Box className="card-container">
                    <Box className="header">
                      <div className="header-content">
                        <Box
                          {...listeners}
                          {...attributes}
                          className="drag-handle">
                          <DragIndicatorIcon />
                          <TextField
                            defaultValue={question.sequence.toString()}
                            onChange={e => {
                              const inputValue = e.target?.value;
                              const maxSequence = questionsData.length;

                              // Restrict input to the maximum sequence length
                              if (parseInt(inputValue) > maxSequence) {
                                e.target.value = '';
                              } else if (parseInt(inputValue) < 1) {
                                e.target.value = '';
                              }
                            }}
                            onBlur={e => {
                              const inputValue = parseInt(e.target.value);
                              const maxSequence = questionsData.length;

                              // Ensure the input value is within bounds
                              if (inputValue > maxSequence) {
                                e.target.value = String(question.sequence);
                              } else if (inputValue < 1 || isNaN(inputValue)) {
                                e.target.value = String(question.sequence);
                              }

                              // Call the handler to update the sequence
                              handleQuestionNumberChange(
                                question.sequence, // From index (sequence is 1-based, array is 0-based)
                                e.target.value,
                                question.id,
                                question?.ModuleOnlineTestList_id
                              );
                            }}
                            style={{width: 30, marginLeft: 10}}
                            inputProps={{
                              style: {textAlign: 'center', padding: '4px'}
                            }}
                          />
                        </Box>
                        {editingQuestionId === String(question.id) ? (
                          <TextField
                            fullWidth
                            value={newQuestionText}
                            onChange={e => setNewQuestionText(e.target.value)}
                            style={{marginRight: 10}}
                          />
                        ) : (
                          <Typography
                            // className="question-text"
                            onClick={() =>
                              handleQuestionId(String(question.id))
                            }
                            sx={{
                              cursor: 'pointer',
                              mr: {xs: 0, sm: 2}, // Margin-right: 0 for small screens, 2 for larger screens
                              ml: {xs: 0, sm: 2},
                              wordBreak: 'break-word', // Ensure text breaks correctly
                              flex: 1,
                              fontSize: {
                                xs: '0.9rem',
                                sm: '1rem',
                                md: '1.2rem'
                              }, // Adjust font size based on screen size
                              textAlign: {xs: 'center', sm: 'left'}, // Center align on small screens
                              whiteSpace: {xs: 'normal', sm: 'nowrap'}, // Wrap text on small screens, no wrap on larger screens
                              minWidth: {xs: '100px', sm: '200px', md: '30vw'} // Responsive min-width
                            }}>
                            {question.QuestionText}
                          </Typography>
                        )}
                      </div>
                      <div className="actions">
                        <Typography variant="caption" style={{marginRight: 10}}>
                          {question.QuestionMarks_id}
                        </Typography>
                        <Box display="flex" alignItems="center">
                          {editingQuestionId === String(question.id) ? (
                            <Button
                              onClick={handleSaveQuestion}
                              variant="contained"
                              color="primary">
                              Save
                            </Button>
                          ) : (
                            <IconButton
                              onClick={() =>
                                handleEditQuestion &&
                                handleEditQuestion(question)
                              }>
                              <EditIcon />
                            </IconButton>
                          )}
                          <IconButton
                            onClick={() => {
                              confirm.onTrue();
                              setDeleteId(question.id);
                            }}>
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </div>
                    </Box>
                    {selectedQuestionId === String(question.id) && (
                      <Box className="options-container">
                        {renderQuestionOptions(question)}
                      </Box>
                    )}
                  </Box>
                )}
              </SortableItem>
            ))}
          </SortableContext>
        </DndContext>
      </Card>
      <Snackbar
        open={saved}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}>
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{width: '40vw'}}>
          Question order updated
        </Alert>
      </Snackbar>
      <ConfirmDialog
        open={confirm.value}
        onClose={() => {
          confirm.onFalse();
          setDeleteId(null);
        }}
        title="Delete"
        content="Are you sure want to delete?"
        action={
          <LoadingButton
            variant="contained"
            loading={deletLoader}
            color="error"
            onClick={() => deleteId !== null && handleDeleteQuestion(deleteId)}>
            Delete
          </LoadingButton>
        }
      />
    </>
  );
};

export default DragDropQuestion;
